import { PANZOOM } from '@/constants.js';
import clipboard from './clipboard.js';
import notifications from './notifications.js';
import contextToolbar from './context-toolbar.js';
import contextMenu from './context-menu.js';
import drag from './drag.js';
import modals from './modals.js';
import appStates from './app-states.js';
import channels from './channels.js';

const state = {
  searchSection: false,
  commentSection: false,
  sidebarSection: 'Components',
  debugSection: false,
  panning: false,
  commentText: null, // The unsaved comment text
  // testing
  cursor: null,
  exactTransform: { scale: 0, x: 0, y: 0 },
  pointerDeviceAutoDetect: true,
  pointerDeviceMode: PANZOOM.MOUSE_MODE.MOUSE,

  /**
   * The subscription plans
   * @type {Array}
   * @default null
   */
  subscriptionPlans: null,
};

const getters = {
  getCursor(state) {
    return state.cursor;
  },
  getExactTransform(state) {
    return state.exactTransform;
  },

  /**
   * Returns whether the search section is open
   * @returns {Boolean}
   */
  getSearchSection(state) {
    return state.searchSection;
  },

  /**
   * Returns whether the comment section is open
   * @returns {Boolean}
   */
  getCommentSection(state) {
    return state.commentSection;
  },

  /**
   * Returns the tab that is open on the sidebar section components/assets
   * @returns {string}
   */
  getSidebarSection(state) {
    return state.sidebarSection;
  },

  /**
   * Returns whether we are panning in the application
   * @returns {Boolean}
   */
  getPanning(state) {
    return state.panning;
  },

  /**
   * Returns the current unsaved comment text
   */
  getCommentText(state) {
    return state.commentText;
  },

  /**
   * Returns the current debug section
   */
  getDebugSection(state) {
    return state.debugSection;
  },

  getPointerDeviceAutoDetect(state) {
    return state.pointerDeviceAutoDetect;
  },

  getPointerDeviceMode(state) {
    return state.pointerDeviceMode;
  },

  getSubscriptionPlans(state) {
    return state.subscriptionPlans;
  },
};

const actions = {
  setCursor(context, { cursor }) {
    context.commit('setCursor', { cursor });
  },

  setExactTransform(context, { transform }) {
    context.commit('setExactTransform', { transform });
  },

  setSearchSection(context, { value }) {
    context.commit('setSearchSection', { value });
  },
  setCommentSection(context, { value }) {
    context.commit('setCommentSection', { value });
  },
  setSidebarSection(context, { value }) {
    context.commit('setSidebarSection', { value });
  },
  setPointerDeviceAutoDetect(context, { value }) {
    context.commit('setPointerDeviceAutoDetect', { value });
  },
  setPointerDeviceMode(context, { value }) {
    context.commit('setPointerDeviceMode', { value });
  },

  setSubscriptionPlans(context, { plans }) {
    context.state.subscriptionPlans = plans;
  },
};

const mutations = {
  setCommentText(state, text) {
    state.commentText = text;
  },

  setCursor(state, { cursor }) {
    state.cursor = cursor;
  },

  setExactTransform(state, { transform }) {
    state.exactTransform = transform;
  },

  /**
   * Open/Close the application comment section
   * @param {Boolean} value
   */
  setSearchSection(state, { value }) {
    state.searchSection = value;
    // The search section and the comment section are mutually exclusive
    if (value) {
      state.commentSection = false;
    }
  },

  /**
   * Open/Close the application comment section
   * @param {Boolean} value
   */
  setCommentSection(state, { value }) {
    state.commentSection = value;
    // The search section and the comment section are mutually exclusive
    if (value) {
      state.searchSection = false;
    }
  },

  setSidebarSection(state, { value }) {
    state.sidebarSection = value;
  },

  /**
   * Set the panning state for the application
   * @param {Boolean} value
   */
  setPanning(state, { value }) {
    state.panning = value;
  },
  setDebugSection(state, { value }) {
    state.debugSection = value;
  },

  setPointerDeviceAutoDetect(state, { value }) {
    state.pointerDeviceAutoDetect = value;
  },
  setPointerDeviceMode(state, { value }) {
    state.pointerDeviceMode = value;
  },

  setSubscriptionPlans(state, { plans }) {
    state.subscriptionPlans = plans;
  },
};

const modules = {
  clipboard,
  notifications,
  contextToolbar,
  contextMenu,
  drag,
  modals,
  appStates,
  channels,
};

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules,
};

export default module;
